import React, { useState, useEffect } from 'react';
import { SR15_RISK_SYSTEMS, SR15RiskSystem } from '../../data/sr15Data';
import { SPM_FIGURE_METADATA } from '../../data/ipccData';
import { AudioEngine } from '../../audio/AudioEngine';
import { FigureDescriptionPanel } from '../FigureDescriptionPanel';
import { Flame, Thermometer, ShieldAlert, Sparkles, Play, Pause } from 'lucide-react';

interface SR15SPM3RisksProps {
  isPlaying: boolean;
  onTogglePlay: () => void;
  comparativeWarming?: '1.5' | '2.0' | null;
}

export const SR15SPM3Risks: React.FC<SR15SPM3RisksProps> = ({
  isPlaying,
  onTogglePlay,
  comparativeWarming
}) => {
  const meta = SPM_FIGURE_METADATA['sr15_spm3'];
  const audioEngine = AudioEngine.getInstance();

  const [tempSlider, setTempSlider] = useState<number>(1.5);
  const [selectedSystemId, setSelectedSystemId] = useState<string>('corals');

  // Override temperature slider if comparative A/B button is triggered
  useEffect(() => {
    if (comparativeWarming === '1.5') setTempSlider(1.5);
    if (comparativeWarming === '2.0') setTempSlider(2.0);
  }, [comparativeWarming]);

  // Audio Update per temperature change
  useEffect(() => {
    if (!audioEngine.getIsUnlocked() || !isPlaying) return;

    const system = SR15_RISK_SYSTEMS.find((s) => s.id === selectedSystemId) || SR15_RISK_SYSTEMS[0];

    // Determine Risk Level (White / Yellow / Red / Purple)
    let riskLevel = 'white';
    if (tempSlider >= system.veryHighThreshold) riskLevel = 'purple';
    else if (tempSlider >= system.highThreshold) riskLevel = 'red';
    else if (tempSlider >= system.moderateThreshold) riskLevel = 'yellow';

    audioEngine.stopAll();

    const baseFreq = audioEngine.tempToFreq(tempSlider);

    if (riskLevel === 'white') {
      // Undetectable: Clean Nat-Warmth sine tone
      audioEngine.startNatWarmth(baseFreq, 0.25);
    } else if (riskLevel === 'yellow') {
      // Moderate Risk: Light LFO FM modulation
      audioEngine.startAnthropoPulse(baseFreq, 3, 0.3);
    } else if (riskLevel === 'red') {
      // High Risk: Dissonant FM pulse + Transient hits
      audioEngine.startAnthropoPulse(baseFreq, 8, 0.4);
      audioEngine.triggerExtremeTransient(baseFreq * 1.5, 0, 0.5);
    } else {
      // Very High Risk (Purple): Bit-crushed Cryo-Hiss + Sub-bass collapse
      audioEngine.startCryoHiss(10000, 0.35);
      audioEngine.startAbyssalDrone(30, 0.5);
      audioEngine.triggerExtremeTransient(baseFreq * 2, 0, 0.7);
    }
  }, [tempSlider, selectedSystemId, isPlaying]);

  const activeSystem = SR15_RISK_SYSTEMS.find((s) => s.id === selectedSystemId) || SR15_RISK_SYSTEMS[0];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 glass-panel p-5 rounded-2xl border border-slate-800">
        <div>
          <span className="text-xs font-mono text-cyan-400 font-semibold">{meta.number}</span>
          <h2 className="text-xl font-extrabold text-white">{meta.title}</h2>
          <p className="text-xs text-slate-400 mt-1">{meta.subtitle}</p>
        </div>

        <button
          onClick={onTogglePlay}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all shadow-lg ${
            isPlaying ? 'bg-amber-500 text-slate-950 glow-amber' : 'bg-cyan-500 text-slate-950 glow-cyan'
          }`}
        >
          {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          <span>{isPlaying ? 'Pause Risk Audition' : 'Play Risk Transition Audio'}</span>
        </button>
      </div>

      {/* Main Burning Embers Module */}
      <div className="glass-panel p-6 rounded-3xl border border-slate-800 space-y-6">
        {/* Global Temperature Slider (+0.5°C to +2.5°C) */}
        <div className="p-5 rounded-2xl bg-slate-950 border border-slate-800 space-y-3">
          <div className="flex justify-between items-center text-xs font-mono">
            <span className="flex items-center gap-2 text-slate-300">
              <Thermometer className="w-4 h-4 text-cyan-400" />
              <span>Global Temperature Anomaly Slider:</span>
            </span>
            <span className="text-2xl font-extrabold text-amber-400">
              +{tempSlider.toFixed(1)}°C
            </span>
          </div>

          <input
            type="range"
            min="0.5"
            max="2.5"
            step="0.1"
            value={tempSlider}
            onChange={(e) => setTempSlider(parseFloat(e.target.value))}
            className="w-full h-3 rounded-lg bg-slate-800 accent-amber-400 cursor-pointer"
          />

          <div className="flex justify-between text-[11px] font-mono text-slate-400">
            <span>+0.5°C</span>
            <span className="text-cyan-400 font-bold">+1.5°C Target</span>
            <span className="text-red-400 font-bold">+2.0°C Target</span>
            <span>+2.5°C</span>
          </div>
        </div>

        {/* System Selector & Burning Embers Grid */}
        <div className="space-y-2">
          <span className="text-xs font-mono text-slate-400 block font-semibold">
            Select Risk System to Audition:
          </span>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {SR15_RISK_SYSTEMS.map((system) => {
              const isSelected = selectedSystemId === system.id;

              let riskBadge = 'White (Undetectable)';
              let badgeColor = 'bg-slate-800 text-slate-300';
              if (tempSlider >= system.veryHighThreshold) {
                riskBadge = 'Purple (Very High Risk)';
                badgeColor = 'bg-purple-950 text-purple-300 border-purple-500/60 glow-purple';
              } else if (tempSlider >= system.highThreshold) {
                riskBadge = 'Red (High Risk)';
                badgeColor = 'bg-red-950 text-red-300 border-red-500/60 glow-red';
              } else if (tempSlider >= system.moderateThreshold) {
                riskBadge = 'Yellow (Moderate Risk)';
                badgeColor = 'bg-amber-950 text-amber-300 border-amber-500/60 glow-amber';
              }

              return (
                <div
                  key={system.id}
                  onClick={() => setSelectedSystemId(system.id)}
                  className={`p-4 rounded-2xl border cursor-pointer transition-all duration-200 glass-panel-hover flex flex-col justify-between space-y-2 ${
                    isSelected
                      ? 'bg-slate-800 border-cyan-400 glow-cyan shadow-md'
                      : 'bg-slate-900/60 border-slate-800 hover:border-slate-700'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <span className="text-xs font-bold text-white leading-snug">{system.name}</span>
                  </div>

                  <span className={`text-[10px] font-mono px-2 py-0.5 rounded-md border inline-block ${badgeColor}`}>
                    {riskBadge}
                  </span>

                  <p className="text-[10px] text-slate-400 leading-tight">{system.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Scientific Synopsis & Original Figure Reference Panel */}
      <FigureDescriptionPanel meta={meta} />
    </div>
  );
};
